import React, { useState } from 'react';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { setBirthdaySet } from '../../../../domains/Auth/slice';
import { Button } from '../../../../GlobalComponents/Button';
import { ButtonContainer, Form } from '../../../../GlobalComponents/Form';
import { Checkbox } from '../../../../GlobalComponents/Checkbox';
import { Input } from '../../../../GlobalComponents/Input';
import { Title } from '../../../../GlobalComponents/Title';
import { ErrorMessage } from '../../../../GlobalComponents/ErrorMessage';
import { InputTextMask } from '../../../../GlobalComponents/InputMask';
import { SuccessMessage } from '../../../../GlobalComponents/SuccessMessage';
import { useAppDispatch, useAppSelector } from '../../../../reduxstore/hooks';
import { useUpdateAccountInfoMutation } from '../../../api';
import { setProfile } from '../../../state/profileSlice';

const AccountDetails = ({
	email,
	phone,
	ewardSubscription,
	freeBirthdayDrink
}: AccountDetailsProps): JSX.Element => {
	const [updateAccountInfo, { isLoading, isError, isSuccess, error }] =
		useUpdateAccountInfoMutation();

	const [formData, setFormData] = useState<AccountDetailsProps>({
		email,
		phone,
		ewardSubscription,
		freeBirthdayDrink,
		errors: null
	});

	// useEffect(() => {
	// 	setFormData({
	// 		email,
	// 		phone,
	// 		ewardSubscription,
	// 		freeBirthdayDrink,
	// 		errors: null
	// 	});
	// }, [email, phone, ewardSubscription, freeBirthdayDrink]);

	const dispatch = useAppDispatch();
	const userId = useAppSelector(state => state.authReducer.id);

	const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const input = event.target.value.replace(' ', '');
		setFormData({
			...formData,
			[event.target.name]: input
		});
	};

	const handlePhoneChanges = (value: string) => {
		setFormData({
			...formData,
			phone: value
		});
	};

	const handleCheckChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({
			...formData,
			[event.target.name]: event.target.checked
		});
	};

	const validateForm = (): boolean => {
		const errors: IErrors = {};
		let formIsValid = true;

		if (isEmpty(formData.email)) {
			formIsValid = false;
			errors['email'] = 'Email cannot be empty';
		} else if (!isEmail(formData.email)) {
			formIsValid = false;
			errors['email'] = 'Invalid Email';
		} else {
			formIsValid = true;
			errors['email'] = null;
		}

		if (isEmpty(`${formData.phone}`)) {
			formIsValid = false;
			errors['phone'] = 'Phone cannot be empty';
		} else if (!isMobilePhone(`${formData.phone}`, 'en-US')) {
			formIsValid = false;
			errors['phone'] = 'Please enter a valid phone number';
		} else if (formData.phone) {
			const isValidPhoneNumber = /^\d{10}$/.test(formData.phone);
			if (!isValidPhoneNumber) {
				errors['phone'] = 'Phone number must be 10 digits.';
			}
		} else {
			formIsValid = true;
			errors['phone'] = null;
		}

		setFormData({
			...formData,
			errors: errors
		});

		return formIsValid;
	};

	const handleSubmit = (event: any): void => {
		event?.preventDefault();

		try {
			if (validateForm()) {
				// data format for API
				const payload = {
					id: userId,
					email: formData.email,
					phone: formData.phone,
					eward_subscription:
						formData.ewardSubscription === true ? 1 : 0
				};
				updateAccountInfo(payload)
					.unwrap()
					.then((res: ProfileResponse) => {
						window.scrollTo({ top: 0, behavior: 'smooth' });
						dispatch(setProfile(res.data.profile));
						dispatch(setBirthdaySet(res.data.birthday_set));
					});
			} else {
				// console.log('Error - validation failed');
			}
		} catch {
			// console.log('error');
		}
	};

	return (
		<>
			<Title>My Account</Title>
			<Form $noColumn={true} onSubmit={handleSubmit}>
				{isError && <ErrorMessage error={error}></ErrorMessage>}
				{isSuccess && (
					<SuccessMessage message="Account details have been successfully updated." />
				)}

				<Input
					name="email"
					label="Email"
					type="email"
					placeholder="Email"
					onChange={handleEmailChange}
					value={formData.email}
					$error={formData.errors ? formData.errors['email'] : null}
				/>

				<InputTextMask
					name="phone"
					label="Phone"
					type="tel"
					inputmode="tel"
					placeholder="Phone"
					value={formData.phone}
					onChange={handlePhoneChanges}
					$error={formData.errors ? formData.errors['phone'] : null}
				/>

				<Checkbox
					name="ewardSubscription"
					label={'I want to receive emailed rewards and coupons!'}
					isChecked={formData.ewardSubscription}
					handleChange={handleCheckChanges}
				/>

				<ButtonContainer>
					<Button
						type="submit"
						$isLoading={isLoading}
						disabled={isLoading}
					>
						SAVE
					</Button>
				</ButtonContainer>
			</Form>
		</>
	);
};

export default AccountDetails;
interface IErrors {
	[key: string]: string | null;
}
interface AccountDetailsProps extends AccountInfo {
	errors?: IErrors | null;
}

interface AccountInfo {
	email: string;
	phone: string;
	ewardSubscription: boolean;
	freeBirthdayDrink: boolean;
}
